// Read the public leaderboard view and keep a safe generated profile name.

import { levelFromCumulativeXp } from "../config/constants.ts";
import { getSupabase } from "./supabase.ts";

const FALLBACK_DISPLAY_NAME = "Player";
const NAME_ADJECTIVES = [
  "Quiet",
  "Lucky",
  "Bright",
  "Calm",
  "Swift",
  "Clever",
  "Gentle",
  "Steady",
] as const;
const NAME_NOUNS = [
  "Ace",
  "King",
  "Queen",
  "Jack",
  "Spade",
  "Heart",
  "Club",
  "Diamond",
] as const;

export interface LeaderboardEntry {
  userId: string;
  displayName: string;
  country: string | null;
  cumulativeXp: number;
  level: number;
  gamesWon: number;
  gamesPlayed: number;
  longestStreakDays: number;
}

export async function fetchLeaderboard(limit = 50): Promise<LeaderboardEntry[]> {
  const sb = getSupabase();
  if (!sb) return [];
  const { data, error } = await sb
    .from("leaderboard")
    .select("*")
    .order("cumulative_xp", { ascending: false })
    .limit(limit);
  if (error) {
    console.warn("[leaderboard] fetch failed:", error.message);
    return [];
  }
  return (data ?? []).map((r: Record<string, unknown>) => ({
    userId: String(r.user_id),
    displayName: String(r.display_name ?? FALLBACK_DISPLAY_NAME),
    country: r.country ? String(r.country) : null,
    cumulativeXp: Number(r.cumulative_xp ?? 0),
    level: levelFromCumulativeXp(Number(r.cumulative_xp ?? 0)).level,
    gamesWon: Number(r.games_won ?? 0),
    gamesPlayed: Number(r.games_played ?? 0),
    longestStreakDays: Number(r.longest_streak_days ?? 0),
  }));
}

export async function ensureGeneratedDisplayName(): Promise<string | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data: sessData } = await sb.auth.getSession();
  const userId = sessData.session?.user.id;
  if (!userId) return null;

  const { data, error } = await sb
    .from("profiles")
    .select("display_name")
    .eq("id", userId)
    .maybeSingle();
  if (error) {
    console.warn("[leaderboard] profile read failed:", error.message);
    return null;
  }

  const current = data?.display_name as string | null | undefined;
  if (isGeneratedDisplayName(current)) return current;

  const generated = generateDisplayName(userId);
  const { error: upsertError } = await sb
    .from("profiles")
    .upsert({ id: userId, display_name: generated }, { onConflict: "id" });
  if (upsertError) {
    console.warn("[leaderboard] generated profile upsert failed:", upsertError.message);
    return null;
  }
  return generated;
}

function isGeneratedDisplayName(name: string | null | undefined): name is string {
  if (!name) return false;
  return /^([A-Z][a-z]+) (Ace|King|Queen|Jack|Spade|Heart|Club|Diamond) #[0-9]{4}$/.test(
    name,
  );
}

function generateDisplayName(userId: string): string {
  const seed = hashString(userId);
  const adjective = NAME_ADJECTIVES[seed % NAME_ADJECTIVES.length]!;
  const noun = NAME_NOUNS[Math.floor(seed / NAME_ADJECTIVES.length) % NAME_NOUNS.length]!;
  const suffix = String(seed % 10_000).padStart(4, "0");
  return `${adjective} ${noun} #${suffix}`;
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
